import { Component, type ReactNode } from 'react';

/**
 * Catches render-phase errors in its subtree and renders a fallback
 * (defaults to `null`). Intended for non-critical UI such as the
 * feedback widget — a missing env var or OC failure should hide the
 * widget silently rather than crash the whole page.
 */
export class SilentErrorBoundary extends Component<
  SilentErrorBoundaryProps,
  SilentErrorBoundaryState
> {
  override state: SilentErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): SilentErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown): void {
    console.warn('[SilentErrorBoundary] swallowed render error:', error);
  }

  override render(): ReactNode {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}

interface SilentErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface SilentErrorBoundaryState {
  hasError: boolean;
}
